/**
 * Generic LRU (Least Recently Used) cache with a fixed capacity.
 * When full, the least recently accessed entry is evicted.
 * An optional `onEvict` callback is called before removal.
 *
 * Used by ClusterManager to keep only nearby clusters in memory.
 */
export class LRUCache<K, V> {
  private _map = new Map<K, V>();
  private _capacity: number;
  private _onEvict: ((key: K, value: V) => void) | null;

  constructor(capacity: number, onEvict?: (key: K, value: V) => void) {
    this._capacity = capacity;
    this._onEvict = onEvict ?? null;
  }

  get size(): number { return this._map.size; }

  has(key: K): boolean {
    return this._map.has(key);
  }

  /** Get a value and mark it as recently used. */
  get(key: K): V | undefined {
    const val = this._map.get(key);
    if (val === undefined) return undefined;

    // Move to end (most recent) by re-inserting
    this._map.delete(key);
    this._map.set(key, val);
    return val;
  }

  /** Insert or update. Evicts oldest if at capacity. */
  set(key: K, value: V): void {
    if (this._map.has(key)) {
      this._map.delete(key);
    } else if (this._map.size >= this._capacity) {
      this._evictOldest();
    }
    this._map.set(key, value);
  }

  /** Iterate all cached values (for rendering). */
  values(): IterableIterator<V> {
    return this._map.values();
  }

  clear(): void {
    if (this._onEvict) {
      for (const [k, v] of this._map) {
        this._onEvict(k, v);
      }
    }
    this._map.clear();
  }

  private _evictOldest(): void {
    const first = this._map.keys().next();
    if (!first.done) {
      const key = first.value;
      const val = this._map.get(key)!;
      this._map.delete(key);
      if (this._onEvict) this._onEvict(key, val);
    }
  }
}
